import React, { FC, useState } from 'react';
import { Box, Grid, Typography, Button, TextField, Select, MenuItem, Autocomplete } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import useStyles from './styles';
import { queueWifiConfiguration, deleteWifiConfiguration } from './calls';
import theme from '../../../globals/theme';
import { enqueueSnackbarAction } from '../../../store/snackbar/actions';
import { AppState } from '../../../store';
import {
  wifiConfigInputData,
  wifiConfigInputDataSecondRow,
  InputsWithValidators,
  validateInputsSendConfiguration,
  wifiConfigInputs,
} from './configuration';
import { countryCode } from './values';

const countryOption = countryCode.map((s) => ({
  value: `${s.value}`,
  label: `${s.value} - ${s.label}`,
}));

const emptyWifiCongfig = (data: InputsWithValidators[]) => {
  const wifiConfigDefaults: { [key: string]: string } = {};
  data.forEach((faultCodeInputDatum: InputsWithValidators) => {
    if (faultCodeInputDatum.defaultValue)
      wifiConfigDefaults[`${faultCodeInputDatum.key}`] = faultCodeInputDatum.defaultValue.value;
  });
  return wifiConfigDefaults;
};
const defaultConfig = emptyWifiCongfig(wifiConfigInputData.concat(wifiConfigInputDataSecondRow));
const DELETE_WIFI_CONFIG = 'DELETE_WIFI_CONFIG';
const WifiConfig: FC = () => {
  const classes = useStyles();
  const login = useSelector((state: AppState) => state.login);
  const selectedEntity = useSelector((state: AppState) => state.search.selectedEntity);
  const [extraRowFlag, setExtraRowFlag] = useState<boolean>(false);
  const [wifiConfiguration, setWifiConfiguration] =
    useState<{ [key: string]: string }>(defaultConfig);
  const dispatch = useDispatch();
  const clearInputs = () => {
    setWifiConfiguration(defaultConfig);
    setExtraRowFlag(false);
  };
  const wifiInputs = selectedEntity?.deviceType
    ? wifiConfigInputs(selectedEntity.deviceType)
    : wifiConfigInputData;
  const snackbarCallback = ({ message, variant }: { message: string; variant: string }) => {
    dispatch(
      enqueueSnackbarAction({
        message,
        options: {
          variant,
        },
      }),
    );
  };
  const removeConfiguration = async () => {
    if (selectedEntity) {
      const result = await deleteWifiConfiguration({
        login,
        selectedEntity,
        wifiConfiguration,
        snackbarCallback,
      });
      if (result) clearInputs();
    }
  };

  const addConfiguration = async () => {
    setExtraRowFlag(!extraRowFlag);
  };

  const sendConfiguration = async () => {
    if (selectedEntity) {
      const result = await queueWifiConfiguration({
        login,
        selectedEntity,
        wifiConfiguration,
        extraRowFlag,
        snackbarCallback,
      });
      if (result) clearInputs();
    }
  };

  const handleChange = (event: any, key: string) => {
    const newFaultCodes: { [key: string]: string } = {
      ...wifiConfiguration,
      [`${key}`]: event?.target?.value,
    };
    setWifiConfiguration(newFaultCodes);
  };

  const disabledSendConfiguration = validateInputsSendConfiguration(
    wifiConfiguration,
    extraRowFlag,
  );

  const renderFormData = (rowData: InputsWithValidators[]) =>
    rowData.map((faultCodeInput: InputsWithValidators) => {
      let value = wifiConfiguration[`${faultCodeInput.key}`];
      const valid = !(
        !value ||
        value?.length === 0 ||
        !faultCodeInput.validator ||
        (faultCodeInput.validator && faultCodeInput.validator(value))
      );
      let inputValues = faultCodeInput.values;
      if (faultCodeInput.key === 'interfaceConfigType' && extraRowFlag) {
        inputValues = undefined;
      }
      if (faultCodeInput.key === 'interfaceConfigType2' && extraRowFlag) {
        const index =
          inputValues &&
          inputValues.findIndex((x) => x.value === wifiConfiguration.interfaceConfigType);

        if (index === 0 && inputValues) {
          value = inputValues[1].value;
        } else if (inputValues) {
          value = inputValues[0].value;
        }
        inputValues = undefined;
        wifiConfiguration.interfaceConfigType2 = value;
      }
      const disabled =
        faultCodeInput.disabled && faultCodeInput.disabled(wifiConfiguration, extraRowFlag);

      return (
        <Grid xs={12} md={6} lg={4} xl={3}>
          <Box className={classes.inputContainer}>
            <Box
              key={faultCodeInput.key}
              p={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box px={1} pb={2}>
                <Typography style={{ color: disabled && theme.palette.grey[400] }} variant="h6">
                  {faultCodeInput.label}:
                </Typography>
              </Box>
              <Box
                p={1}
                height={67}
                display="flex"
                alignItems="flex-start"
                justifyContent="flex-end"
                width="100%"
              >
                {!inputValues && (
                  <Box mt={-2} width="100%" display="flex" justifyContent="flex-end">
                    <TextField
                      variant="standard"
                      disabled={!!disabled}
                      value={value}
                      error={valid}
                      helperText={valid && faultCodeInput.helperText}
                      className={clsx(classes.textLabel, classes.fullWidth)}
                      id={classes.textLabel}
                      label={faultCodeInput.label}
                      onChange={(event) => handleChange(event, faultCodeInput.key)}
                    />
                  </Box>
                )}

                {faultCodeInput && faultCodeInput.key === "countryCode" && (
                  <Autocomplete
                    className={classes.fullWidth}
                    options={countryOption}
                    renderInput={(parameters) => <TextField variant="standard" {...parameters} />}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setWifiConfiguration({ ...wifiConfiguration, 'countryCode': newValue.value })
                      }
                    }}
                  />
                )}

                {inputValues && faultCodeInput && faultCodeInput.key !== "countryCode" && (
                  <Select
                    variant="standard"
                    className={classes.fullWidth}
                    disabled={!!disabled}
                    onChange={(event) => handleChange(event, faultCodeInput.key)}
                    labelId={faultCodeInput.label}
                    id="select"
                    error={false}
                    value={value}
                  >
                    {faultCodeInput.values &&
                      faultCodeInput.values.map((option) => (
                        <MenuItem value={option.value}>{option.label}</MenuItem>
                      ))}
                  </Select>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      );
    });

  return (
    <Box p={1} className={classes.noWrap} display="flex" flexDirection="column" width="100%">
      <Box display="flex" flexWrap="wrap" justifyContent="space-between">
        {renderFormData(wifiInputs)}
      </Box>
      <Box display="flex" flexWrap="wrap" justifyContent="space-between">
        {extraRowFlag && renderFormData(wifiConfigInputDataSecondRow)}
      </Box>
      <Box display="flex" alignItems="center" flexGrow={1} justifyContent="flex-end" pr={2}>
        <Box>
          {wifiConfiguration.command !== DELETE_WIFI_CONFIG && (
            <Button
              id="upload-add-to-queue'"
              onClick={addConfiguration}
              className={classes.leftButton}
              variant="outlined"
              size="small"
              color="primary"
            >
              {!extraRowFlag && 'Add Configuration'}
              {extraRowFlag && 'Cancel'}
            </Button>
          )}
          {wifiConfiguration.command !== DELETE_WIFI_CONFIG && (
            <Button
              disabled={disabledSendConfiguration}
              id="upload-add-to-queue'"
              onClick={sendConfiguration}
              variant="outlined"
              size="small"
              color="primary"
            >
              Send Configuration
            </Button>
          )}
          {wifiConfiguration.command === DELETE_WIFI_CONFIG && (
            <Button
              id="upload-add-to-queue'"
              onClick={removeConfiguration}
              variant="outlined"
              size="small"
              color="primary"
            >
              Remove Configuration
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WifiConfig;
